import moment from 'moment'
import { BadgeVariant } from 'core/elements/badge/Badge'
import Text from 'core/elements/Text'
import calcUsageTotalByPath from 'k8s/util/calcUsageTotals'
import { getIPAddresses, getRoleStatus, ServiceStatus } from './selector'

export const serviceHealthMap = {
  Healthy: 'success',
  Unhealthy: 'error',
  Connected: 'success',
  Not_Connected: 'error',
  Configured: 'success',
  Not_Configured: 'error',
  'Partially Healthy': 'warning',
  Maintenance: 'warning',
  Warning: 'warning',
  'N/A': 'default',
  Unknown: 'unknown',
  NetworkError: 'warning',
  CertificateError: 'warning',
  'Scheduling Disabled': 'warning',

  // Connectivity status
  ok: 'success',
  unauthorized: 'success',
  converging: 'warning',
  failed: 'error',
  error: 'error',
  offline: 'error',
  online: 'success',
}

export const healthLableMap = {
  Healthy: 'Healthy',
  Unhealthy: 'Unhealthy',
  Connected: 'Connected',
  Not_Connected: 'Not Connected',
  Configured: 'Configured',
  Not_Configured: 'Not Configured',
  Maintenance: 'Maintenance',
  'N/A': 'Not Configured',
  Unknown: 'Unknown',
  Warning: 'Warning',
  NetworkError: 'NetworkError',
  CertificateError: 'Trust Certificate',
  'Partially Healthy': 'Partially Healthy',

  // Connectivity status
  ok: 'Ok',
  converging: 'Converging',
  failed: 'Failed',
  error: 'Error',
  offline: 'Offline',
  unauthorized: 'Unauthorized',
  online: 'Online',
}

export const healthTooltipMap = {
  [healthLableMap.Healthy]:
    'Service is operating normally with all hosts responding. No action required.',
  [healthLableMap.Unhealthy]:
    'Service has critical issues affecting operations. Multiple hosts offline or degraded. Immediate investigation required.',
  ['Partially Healthy']:
    'Service is operational but some hosts are experiencing issues. Performance may be reduced. Monitor and investigate affected hosts.',
  [healthLableMap.Warning]:
    'Service is functional but has minor issues or reduced capacity. One or more hosts offline or degraded. Monitor closely.',
  [healthLableMap.Unknown]:
    'Unable to determine service health. Service may not be reporting status or health checks are failing. Verify service configuration.',
  [healthLableMap.Connected]:
    'Service is successfully communicating with management plane. Heartbeats received within expected intervals.',
  [healthLableMap.Not_Connected]:
    'Service has lost connection to management plane. Heartbeat timeout exceeded. Check network connectivity and service status.',
  [healthLableMap.Configured]:
    'Service is properly configured on the host and ready for operations. All required components and settings are in place.',
  [healthLableMap.Not_Configured]:
    'Service configuration is incomplete or invalid. Complete setup required before service can function. Review configuration requirements.',
  [healthLableMap.Maintenance]:
    'Service is in planned maintenance mode. New operations may be restricted. This is expected behavior during updates or repairs.',
  [healthLableMap.NetworkError]:
    'Network connectivity issues preventing service communication. Check network configuration, firewall rules, and DNS resolution.',
  [healthLableMap.CertificateError]:
    'SSL/TLS certificate validation failed. You need to accept the self signed certificate for the service to operate.',
}

export type ServiceState = { healthy: number; unhealthy: number; warning: number; total: number }

export const formatTime = (timestamp: string | number | Date): string => {
  return moment(timestamp).format('hh:mm:ss A')
}

export const serviceStateCountHelper = (services: any[]) => {
  return services.reduce(
    (accum, service) => {
      if (service?.status === ServiceStatus.Healthy) accum.healthy += 1
      if (service?.status === ServiceStatus.CertificateError) accum.certificateError += 1
      if (service?.status === ServiceStatus.Unhealthy) accum.unhealthy += 1
      if (service?.status === ServiceStatus.Maintenance) accum.maintenance += 1
      if (service?.status === ServiceStatus.SchedulingDisabled) accum.schedulingDisabled += 1
      if (service?.status === ServiceStatus.NetworkError) accum.networkError += 1
      return accum
    },
    {
      healthy: 0,
      unhealthy: 0,
      maintenance: 0,
      schedulingDisabled: 0,
      networkError: 0,
      unauthorized: 0,
      certificateError: 0,
    },
  )
}

export const hostStateCountHelper = (
  hosts: {
    computeServicesStatus?: string
    storageServiceStatus?: string
    networkServiceStatus?: string
    imageServiceStatus?: string
  }[],
) => {
  return hosts.reduce(
    (accum, host) => {
      const relevantStatuses = Object.values(host).filter(
        (status) => typeof status === 'string' && status.toLowerCase() !== 'n/a',
      )

      if (relevantStatuses.length === 0) return accum // Ignore records with only 'N/A'

      const isUnhealthy = relevantStatuses.some((status) => status.toLowerCase() === 'unhealthy')
      const isWarning = relevantStatuses.some((status) =>
        ['networkerror', 'maintenance'].includes(status.toLowerCase()),
      )

      if (isUnhealthy) {
        accum.unhealthy += 1
      } else if (isWarning) {
        accum.warning += 1
      } else {
        accum.healthy += 1
      }

      return accum
    },
    { healthy: 0, warning: 0, unhealthy: 0 },
  )
}

// Aggregate the service states based on the service type
export const aggregateServiceStateOnServiceType = (states: { statusHealth: string }[]) =>
  states?.reduce(
    (acc, curr) => {
      acc.healthy += curr.statusHealth === serviceHealthMap.Healthy ? 1 : 0
      acc.unhealthy += curr.statusHealth === serviceHealthMap.Unhealthy ? 1 : 0
      acc.warning += curr.statusHealth === serviceHealthMap.Warning ? 1 : 0
      acc.total += 1 // Update total count
      return acc
    },
    { healthy: 0, unhealthy: 0, warning: 0, total: 0 },
  )

// Aggregate the states of all services
export const aggregateServiceStates = (states): ServiceState =>
  states.reduce(
    (acc, curr) => {
      acc.healthy += curr.healthy || 0
      acc.unhealthy += curr.unhealthy || 0
      acc.warning += curr.warning || 0
      acc.total = acc.healthy + acc.unhealthy + acc.warning // Update total count
      return acc
    },
    { healthy: 0, unhealthy: 0, warning: 0, total: 0 },
  )

export const mergeHosts = (arrays) => {
  const mergedMap = new Map()

  arrays.flat().forEach((obj) => {
    const hostKey = obj.hostDetails?.id

    if (!mergedMap.has(hostKey)) {
      mergedMap.set(hostKey, {
        ...obj,
        computeStatus: obj.computeServicesStatus || 'N/A',
        storageStatus: obj.storageServiceStatus || 'N/A',
        networkStatus: obj.networkServiceStatus || 'N/A',
        imageStatus: obj.imageServiceStatus || 'N/A',
      })
    } else {
      const existing = mergedMap.get(hostKey)

      // Merge objects while ensuring unique fields are retained
      Object.keys(obj).forEach((key) => {
        if (!existing[key]) {
          existing[key] = obj[key] // Keep new fields
        } else if (typeof obj[key] === 'object' && !Array.isArray(obj[key])) {
          // For nested objects, keep only one instance (like hostDetails)
          existing[key] = existing[key] || obj[key]
        }
      })

      // Ensure the status fields are updated based on availability
      existing.computeStatus = existing.computeServicesStatus || 'N/A'
      existing.storageStatus = existing.storageServiceStatus || 'N/A'
      existing.networkStatus = existing.networkServiceStatus || 'N/A'
      existing.imageStatus = existing.imageServiceStatus || 'N/A'

      mergedMap.set(hostKey, existing)
    }
  })

  return Array.from(mergedMap.values())
}

export const calculateNodeUsages = (nodes) => {
  const calcNodesTotals = calcUsageTotalByPath(nodes)
  return {
    compute: calcNodesTotals('usage.compute.current', 'usage.compute.max'),
    memory: calcNodesTotals('usage.memory.current', 'usage.memory.max'),
    disk: calcNodesTotals('usage.disk.current', 'usage.disk.max'),
  }
}

export const IPAddressCellComponent = ({ value }) => {
  return (
    <div>
      {value?.map((iface) => (
        <Text key={iface?.ip} variant="body2">
          {iface?.ip} ({iface?.name})
        </Text>
      ))}
    </div>
  )
}

export const getHealthStatus = (phase: string) => {
  const status: BadgeVariant = serviceHealthMap[phase] || 'unknown'
  const label = healthLableMap[phase] || phase
  const tooltipBody = healthTooltipMap[label] || ''
  return { variant: status, label, tooltipBody }
}

export const getConnectivityStatus = (phase: string, tooltipBody?: string) => {
  const status: BadgeVariant = serviceHealthMap[phase] || 'error'
  const label = healthLableMap[phase] || phase
  return tooltipBody ? { variant: status, label, tooltipBody } : { variant: status, label }
}

// Get the aggregated Service State based on individual service state
export const getOverallServiceStatus = (
  serviceStates,
  isComputeService?: boolean,
  serviceDetails?: any[],
) => {
  let statusHealth, statusName

  if (isComputeService) {
    // Doc - https://platform9.atlassian.net/wiki/spaces/PM/pages/4757389316/PCD+Service+Health+Dashboard+and+Host+Health+Reporting#Compute-service-health
    // If any compute service is unhealthy, return unhealthy immediately
    if (serviceStates?.unhealthy > 0) {
      return {
        statusHealth: serviceHealthMap.Unhealthy,
        statusName: healthLableMap.Unhealthy,
      }
    }

    // If there are no hosts, return unknown status
    const totalHosts = serviceDetails?.length ?? 0

    if (totalHosts === 0) {
      return {
        statusHealth: serviceHealthMap.Unknown,
        statusName: healthLableMap.Unknown,
      }
    }

    // If any compute service is unhealthy, return unhealthy immediately
    const unhealthyHosts =
      serviceDetails?.filter(
        (service: any) => service?.computeServicesStatus === ServiceStatus.Unhealthy,
      ).length ?? 0

    if (unhealthyHosts > 0) {
      return {
        statusHealth: serviceHealthMap.Unhealthy,
        statusName: healthLableMap.Unhealthy,
      }
    }

    // For compute services, we also need to consider offline hosts i.e connectivity status of hosts is offline
    // If the number of hosts in offline state >= 50%, compute health service  = unhealthy
    // If 25% < hosts in offline state < 50%, compute health service = warning
    // If hosts in offline state <= 25%, compute health service = healthy
    const offlineHosts =
      serviceDetails?.filter((service: any) => service?.connectivityStatus === 'offline')?.length ??
      0
    const offlineHostPercent = (offlineHosts / totalHosts) * 100

    if (offlineHostPercent >= 50) {
      statusHealth = serviceHealthMap.Unhealthy
      statusName = healthLableMap.Unhealthy
    } else if (offlineHostPercent > 25 && offlineHostPercent < 50) {
      statusHealth = serviceHealthMap.Warning
      statusName = healthLableMap.Warning
    } else {
      statusHealth = serviceHealthMap.Healthy
      statusName = healthLableMap.Healthy
    }

    return { statusHealth, statusName }
  }

  statusHealth =
    serviceStates?.unhealthy > 0
      ? serviceHealthMap.Unhealthy
      : serviceStates?.networkError > 0
        ? serviceHealthMap.NetworkError
        : serviceStates?.certificateError > 0
          ? serviceHealthMap.CertificateError
          : serviceStates?.healthy > 0
            ? serviceHealthMap.Healthy
            : serviceHealthMap.Unknown
  statusName =
    serviceStates?.unhealthy > 0
      ? healthLableMap.Unhealthy
      : serviceStates?.networkError > 0
        ? healthLableMap.NetworkError
        : serviceStates?.certificateError > 0
          ? healthLableMap.CertificateError
          : serviceStates?.healthy > 0
            ? healthLableMap.Healthy
            : healthLableMap.Unknown

  return { statusHealth, statusName }
}

// Filter out services that do not have host details
export const validServiceDetails = (services: any[]) => {
  return services.filter((service) => !!service?.hostDetails)
}

export const getMergedStorageServiceDetails = (storageServiceDetails) => {
  const reducedData = storageServiceDetails.reduce(
    (accum, service) => {
      if (accum.statusesByHostId?.[service.hostDetails?.id]) {
        return {
          ...accum,
          statusesByHostId: {
            ...accum.statusesByHostId,
            [service.hostDetails.id]: [
              ...accum.statusesByHostId[service.hostDetails.id],
              { volumeBackend: service.volumeBackend, status: service.status },
            ],
          },
        }
      } else {
        return {
          firstServiceOfHost: [...accum.firstServiceOfHost, service],
          statusesByHostId: {
            ...accum.statusesByHostId,
            [service.hostDetails?.id]: [
              {
                volumeBackend: service.volumeBackend,
                status: service.status,
              },
            ],
          },
        }
      }
    },
    {
      firstServiceOfHost: [],
      statusesByHostId: {},
    },
  )
  const { firstServiceOfHost, statusesByHostId } = reducedData
  return firstServiceOfHost.map((service) => {
    return {
      ...service,
      volumeBackendStatuses: statusesByHostId[service.hostDetails?.id],
    }
  })
}

export interface LoadingServices {
  compute: boolean
  storage: boolean
  network: boolean
  image: boolean
}

export const LOADING_STATUS = 'Loading...'

export const buildCompleteCombinedServices = (
  validCombinedServicesDetails: any[],
  hosts: any[],
  loadingServices: LoadingServices,
): any[] => {
  const usedHostIds = new Set(validCombinedServicesDetails?.map((item) => item?.host))

  const anyServiceLoading = Object.values(loadingServices).some((value) => value)
  const mappedValidCombinedServicesDetails = anyServiceLoading
    ? validCombinedServicesDetails.map((service) => ({
        ...service,
        computeStatus: loadingServices?.compute ? LOADING_STATUS : service.computeStatus,
        storageStatus: loadingServices?.storage ? LOADING_STATUS : service.storageStatus,
        networkStatus: loadingServices?.network ? LOADING_STATUS : service.networkStatus,
        imageStatus: loadingServices?.image ? LOADING_STATUS : service.imageStatus,
      }))
    : validCombinedServicesDetails

  const missingHosts = hosts
    ?.filter(
      (host) =>
        !usedHostIds.has(host?.id) && Array.isArray(host?.roles) && host?.roles?.length === 0,
    )
    ?.map((host) => ({
      name: host?.info?.hostname,
      clusterName: host?.clusterName || 'N/A',
      host: host?.id,
      connectivityStatus: host?.info?.responding ? 'ok' : 'unauthorized',
      computeStatus: loadingServices?.compute ? LOADING_STATUS : 'N/A',
      imageStatus: loadingServices?.image ? LOADING_STATUS : 'N/A',
      storageStatus: loadingServices?.storage ? LOADING_STATUS : 'N/A',
      networkStatus: loadingServices?.network ? LOADING_STATUS : 'N/A',
      ipAddresses: getIPAddresses(host),
      roleStatus: getRoleStatus(host),
      ...host,
    }))

  return [...mappedValidCombinedServicesDetails, ...missingHosts]
}

// A network missing its distributed (metadata) port.
export interface MetadataNetwork {
  id: string
  name: string
  projectId: string
}

// Raw counts from the cheap counts_only queries.
export interface MetadataHealthCounts {
  total: number
  working: number
}

export interface MetadataHealthSummary {
  total: number
  healthy: number
  broken: number
  status: ServiceStatus
}

// Summarize cloud-init metadata health from the network vs distributed-port counts. A
// network without a distributed port has broken metadata routing, so any shortfall of
// distributed ports means some networks are broken and the overall status is unhealthy.
// Missing/errored counts default to 0 (no broken networks) rather than crashing.
// See MetadataServiceHealth.spec.md (MSH-FR-003, MSH-FR-005).
export const getMetadataHealthSummary = ({
  total = 0,
  working = 0,
}: Partial<MetadataHealthCounts> = {}): MetadataHealthSummary => {
  const safeTotal = total || 0
  const safeWorking = working || 0
  // Clamp so a working count that exceeds the total (e.g. multiple distributed ports on a
  // network) can never produce a negative broken count.
  const broken = Math.max(0, safeTotal - safeWorking)
  return {
    total: safeTotal,
    healthy: safeTotal - broken,
    broken,
    status: broken > 0 ? ServiceStatus.Unhealthy : ServiceStatus.Healthy,
  }
}

// Diff the full networks list against the full distributed-ports list to find exactly
// which networks have no distributed (metadata) port. Distributed ports are deduped by
// network_id so a network with several distributed ports is only ever counted once.
// Missing/errored inputs default to empty (no broken networks).
// See MetadataServiceHealth.spec.md (MSH-FR-002, MSH-FR-004, MSH-FR-005).
export const getBrokenMetadataNetworks = (
  networks: any[] = [],
  distributedPorts: any[] = [],
): MetadataNetwork[] => {
  const networksWithMetadataPort = new Set(
    (distributedPorts || []).map((port) => port?.network_id).filter(Boolean),
  )
  return (networks || [])
    .filter((network) => !networksWithMetadataPort.has(network?.id))
    .map((network) => ({
      id: network?.id,
      name: network?.name,
      projectId: network?.project_id || network?.tenant_id,
    }))
}
